import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { useAppStore } from '../state/store';
import type { EquityCurveResult } from '../state/types';
import { getCanonicalTimestampsForAllCharts, padSeriesToTimeline } from '../lib/chartTimeline';
import { applyRange, RANGE_PRESETS } from '../lib/chartRangePresets';
import type { ChartTimeSyncPreset } from '../state/store';
import {
  chartPanPlugin,
  CHART_RIGHT_AXIS_SIZE,
  createXAxisConfig,
  findIndexForTimestamp,
  getChartCursorSyncConfig,
  xAxisSecondRowPlugin,
} from './PrimarySeriesChart';
import { useSyncedCrosshair } from '../hooks/useSyncedCrosshair';

const CHART_HEIGHT = 200;
const CASH_FILL = '#5a6e3a';
const EQUITY_FILL = '#3a4e6e';
const CASH_STROKE = '#4a5a2e';
const EQUITY_STROKE = '#2e3a5a';

const BAR_WIDTH_FACTOR = 0.6;

/**
 * uPlot plugin: stacked bars per index. data[1]=cash, data[2]=total.
 * Each bar: total height = account value; bottom segment = cash, top segment = position equity.
 */
function stackedBarsPlugin() {
  function draw(u: uPlot) {
    u.ctx.save();
    const { width } = u.bbox;
    const cash = u.data[1] as number[];
    const total = u.data[2] as number[];
    if (!cash || !total) {
      u.ctx.restore();
      return;
    }
    const xMin = u.scales.x.min ?? 0;
    const xMax = u.scales.x.max ?? cash.length - 1;
    const iMin = Math.max(0, Math.floor(xMin));
    const iMax = Math.min(cash.length - 1, Math.ceil(xMax));
    const visibleBarCount = Math.max(1, iMax - iMin + 1);
    const pixelsPerBar = width / visibleBarCount;

    for (let i = iMin; i <= iMax; i++) {
      const xVal = u.scales.x.distr === 2 ? i : (u.data[0] as number[])[i];
      const cashVal = cash[i] ?? 0;
      const totalVal = total[i];
      if (totalVal == null || !Number.isFinite(totalVal)) continue;

      const xCenter = u.valToPos(xVal, 'x', true);
      const barPxWidth = Math.max(1, Math.round(pixelsPerBar * BAR_WIDTH_FACTOR));
      const barLeft = Math.round(xCenter - barPxWidth / 2);

      const y0 = u.valToPos(0, 'y', true);
      const yTotal = u.valToPos(totalVal, 'y', true);
      const yCash = u.valToPos(Math.max(0, cashVal), 'y', true);

      // Pixel y increases downward. Bar: 0 at bottom, total at top. Cash = 0..cash, equity = cash..total.
      const cashYTop = Math.min(yCash, y0);
      const cashYBottom = Math.max(yCash, y0);
      const eqYTop = Math.min(yTotal, yCash);
      const eqYBottom = Math.max(yTotal, yCash);

      // Cash segment (bottom of bar)
      if (cashYBottom > cashYTop) {
        u.ctx.fillStyle = CASH_FILL;
        u.ctx.fillRect(barLeft, Math.round(cashYTop), barPxWidth, Math.round(cashYBottom - cashYTop));
      }
      // Equity segment (top of bar)
      if (eqYBottom > eqYTop) {
        u.ctx.fillStyle = EQUITY_FILL;
        u.ctx.fillRect(barLeft, Math.round(eqYTop), barPxWidth, Math.round(eqYBottom - eqYTop));
      }
    }
    u.ctx.restore();
  }

  return {
    opts: (_u: uPlot, baseOpts: uPlot.Options) => {
      baseOpts.series.forEach((s, i) => {
        if (i > 0) {
          (s as uPlot.Series).paths = () => null;
          (s as uPlot.Series).points = { show: false };
        }
      });
    },
    hooks: { draw },
  };
}

function toUnixSeconds(ts: string): number {
  const s = ts.slice(0, 10);
  return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 1000);
}

function formatLegendDate(timestamps: number[], idx: number): string {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const sec = timestamps[i];
  if (sec == null) return '';
  const d = new Date(sec * 1000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

function buildChartData(
  curve: EquityCurveResult,
  canonicalTimestamps: number[]
): { data: uPlot.AlignedData; timestamps: number[] } {
  const equityTimestamps = curve.timestamps.map(toUnixSeconds);
  const cashRounded = curve.cash_per_bar.map((v) => Math.round(v * 100) / 100);
  const totalRounded = curve.total_per_bar.map((v) => Math.round(v * 100) / 100);
  const paddedCash = padSeriesToTimeline(
    canonicalTimestamps,
    equityTimestamps,
    cashRounded,
    null
  );
  const paddedTotal = padSeriesToTimeline(
    canonicalTimestamps,
    equityTimestamps,
    totalRounded,
    null
  );
  const n = canonicalTimestamps.length;
  const data: uPlot.AlignedData = [
    Array.from({ length: n }, (_, i) => i),
    paddedCash,
    paddedTotal,
  ];
  return { data, timestamps: canonicalTimestamps };
}

export default function EquityCurveChart() {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const timestampsRef = useRef<number[]>([]);
  const ohlc = useAppStore((s) => s.ohlc);
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const backtestEquityCurve = useAppStore((s) => s.backtestEquityCurve);
  const chartTimeSync = useAppStore((s) => s.chartTimeSync);
  const chartTimeSyncPreset = useAppStore((s) => s.chartTimeSyncPreset);
  const setChartTimeRange = useAppStore((s) => s.setChartTimeRange);
  const setChartVisibleTimeRange = useAppStore((s) => s.setChartVisibleTimeRange);
  const setChartTimeSyncPreset = useAppStore((s) => s.setChartTimeSyncPreset);
  const primaryChartFullWidth = useAppStore((s) => s.primaryChartFullWidth);

  useSyncedCrosshair(
    'equity',
    chartRef,
    timestampsRef,
    (ts) => findIndexForTimestamp(timestampsRef.current, ts ?? 0) ?? undefined
  );

  useEffect(() => {
    if (!containerRef.current || !backtestEquityCurve) return;

    const equityTs = backtestEquityCurve.timestamps.map(toUnixSeconds);
    const instrumentIds = instruments.map((i) => i.id);
    const canonicalTimestamps = getCanonicalTimestampsForAllCharts(
      ohlc,
      primaryInstrumentId,
      instrumentIds,
      equityTs.length > 0 ? equityTs : undefined
    );
    if (canonicalTimestamps.length === 0) return;

    const { data, timestamps } = buildChartData(
      backtestEquityCurve,
      canonicalTimestamps
    );
    timestampsRef.current = timestamps;
    if (data[0].length === 0) return;

    const n = data[0].length - 1;
    const sync = useAppStore.getState().chartTimeSync;
    let xMin = 0;
    let xMax = Math.max(0, n);
    if (sync.xMin != null && sync.xMax != null) {
      xMin = Math.max(0, Math.min(sync.xMin, n));
      xMax = Math.max(0, Math.min(sync.xMax, n));
      if (xMin >= xMax) {
        xMin = 0;
        xMax = n;
      }
    }

    const fullW = useAppStore.getState().primaryChartFullWidth;
    const width =
      fullW != null
        ? fullW
        : Math.max(containerRef.current.clientWidth, 100);
    const cash = data[1] as (number | null)[];
    const total = data[2] as (number | null)[];
    const yMin = 0;
    const finiteTotal = total.filter((v): v is number => v != null && Number.isFinite(v));
    const finiteCash = cash.filter((v): v is number => v != null && Number.isFinite(v));
    const yMax = Math.max(1, ...finiteTotal, ...finiteCash);

    const opts: uPlot.Options = {
      width,
      height: CHART_HEIGHT,
      plugins: [
        stackedBarsPlugin(),
        chartPanPlugin(
          (min, max) => useAppStore.getState().setChartTimeRange(min, max),
          undefined,
          getChartCursorSyncConfig('equity', (_u, idx) => timestamps[idx] ?? null)
        ),
        xAxisSecondRowPlugin(timestamps),
      ],
      series: [
        {
          label: 'Date',
          value: (_u, _rawValue, _seriesIdx, idx) =>
            idx != null ? formatLegendDate(timestamps, idx) : '--',
        },
        {
          label: 'Cash',
          scale: 'y',
          stroke: CASH_STROKE,
          fill: CASH_FILL,
        },
        {
          label: 'Total',
          scale: 'y',
          stroke: EQUITY_STROKE,
          fill: EQUITY_FILL,
          fillTo: () => 1,
        },
      ],
      scales: {
        x: { min: xMin, max: xMax, distr: 2 },
        y: { min: yMin, max: yMax, auto: false },
      },
      axes: [
        createXAxisConfig(timestamps),
        {
          scale: 'y',
          side: 1,
          size: CHART_RIGHT_AXIS_SIZE,
          values: (_u, vals) =>
            vals.map((v) => (v != null && Number.isFinite(v) ? (v as number).toFixed(0) : '')),
        },
      ],
    };

    const chart = new uPlot(opts, data, containerRef.current);
    chartRef.current = chart;

    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const fullW = useAppStore.getState().primaryChartFullWidth;
      const w =
        fullW != null
          ? fullW
          : containerRef.current.clientWidth;
      chartRef.current.setSize({ width: w, height: CHART_HEIGHT });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.destroy();
      chartRef.current = null;
    };
  }, [backtestEquityCurve, ohlc, primaryInstrumentId, instruments]);

  // Resize equity chart when primary full width changes so vertical crosshairs stay aligned
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || primaryChartFullWidth == null) return;
    chart.setSize({
      width: primaryChartFullWidth,
      height: CHART_HEIGHT,
    });
  }, [primaryChartFullWidth]);

  // Apply synced x range when store updates (e.g. from primary range/pan)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chartTimeSync.xMin == null || chartTimeSync.xMax == null) return;
    const n = (chart.data[0] as number[]).length - 1;
    const xMin = Math.max(0, Math.min(chartTimeSync.xMin, n));
    const xMax = Math.max(0, Math.min(chartTimeSync.xMax, n));
    if (xMin >= xMax) return;
    if (chart.scales.x.min === xMin && chart.scales.x.max === xMax) return;
    chart.setScale('x', { min: xMin, max: xMax });
  }, [chartTimeSync.xMin, chartTimeSync.xMax]);

  function handleRangeSelect(preset: ChartTimeSyncPreset) {
    const chart = chartRef.current;
    const timestamps = timestampsRef.current;
    if (!chart) {
      setChartTimeSyncPreset(preset);
      return;
    }
    const n = (chart.data[0] as number[]).length;
    if (n === 0) return;
    const rightEdgeIndex =
      chart.scales.x.min != null && chart.scales.x.max != null
        ? Math.max(0, Math.min(n - 1, Math.round(chart.scales.x.max)))
        : n - 1;
    const xRange = applyRange(chart, n, preset, rightEdgeIndex);
    setChartTimeRange(xRange.min, xRange.max);
    if (timestamps.length > 0) {
      setChartVisibleTimeRange(
        timestamps[Math.min(xRange.min, timestamps.length - 1)],
        timestamps[Math.min(xRange.max, timestamps.length - 1)]
      );
    }
    setChartTimeSyncPreset(preset);
  }

  if (!backtestEquityCurve) return null;

  const maxCap = backtestEquityCurve.max_capital_required;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold font-headline text-ink">Equity Curve</h3>
          {maxCap > 0 && (
            <span className="text-sm text-ink/80">
              Min capital required: <strong className="text-ink">{maxCap.toFixed(2)}</strong>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {RANGE_PRESETS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleRangeSelect(id)}
              className={
                'px-2 py-1 text-xs rounded-sm border transition-colors ' +
                (chartTimeSyncPreset === id
                  ? 'bg-ink text-newsprint border-ink'
                  : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div
        ref={containerRef}
        className="w-full"
        style={{
          height: CHART_HEIGHT,
          ...(primaryChartFullWidth != null && { width: primaryChartFullWidth }),
        }}
      />
      <div className="flex gap-4 text-sm text-ink/70">
        <span style={{ color: CASH_STROKE }}>■</span> Cash
        <span style={{ color: EQUITY_STROKE }}>■</span> Position equity
      </div>
      <EquityCurveSummaryStats curve={backtestEquityCurve} />
    </div>
  );
}

function EquityCurveSummaryStats({ curve }: { curve: EquityCurveResult }) {
  const total = curve.total_per_bar;
  const timestamps = curve.timestamps;
  if (total.length === 0) return null;

  const startingCapital = total[0];
  const terminalCapital = total[total.length - 1];
  const totalReturnPct =
    startingCapital > 0 && Number.isFinite(startingCapital)
      ? ((terminalCapital - startingCapital) / startingCapital) * 100
      : null;
  let annualizedReturnPct: number | null = null;
  if (
    timestamps.length >= 2 &&
    startingCapital > 0 &&
    terminalCapital > 0 &&
    Number.isFinite(startingCapital) &&
    Number.isFinite(terminalCapital)
  ) {
    const firstTs = new Date(timestamps[0].slice(0, 10) + 'T00:00:00Z').getTime();
    const lastTs = new Date(timestamps[timestamps.length - 1].slice(0, 10) + 'T00:00:00Z').getTime();
    const years = (lastTs - firstTs) / (365.25 * 24 * 60 * 60 * 1000);
    if (years > 0) {
      annualizedReturnPct = (Math.pow(terminalCapital / startingCapital, 1 / years) - 1) * 100;
    }
  }

  const fmtCap = (v: number) => v.toFixed(2);
  const fmtPct = (v: number) => v.toFixed(2) + '%';

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink/70">
      <span>
        Starting Capital: <strong className="text-ink">{fmtCap(startingCapital)}</strong>
      </span>
      <span>
        Terminal Capital: <strong className="text-ink">{fmtCap(terminalCapital)}</strong>
      </span>
      <span>
        Total Return:{' '}
        <strong className="text-ink">
          {totalReturnPct != null ? fmtPct(totalReturnPct) : '—'}
        </strong>
      </span>
      <span>
        Annualized Return:{' '}
        <strong className="text-ink">
          {annualizedReturnPct != null ? fmtPct(annualizedReturnPct) : '—'}
        </strong>
      </span>
    </div>
  );
}
